import React, { useState, useEffect } from 'react';

import { ItemsService } from './services/itemsService';
import ErrorBoundary from './components/ErrorBoundary';
import { itemsSync } from './utils';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useNavigate, useLocation } from 'react-router-dom';

import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import { NotificationProvider } from './contexts/NotificationContext';
import type { Project } from './types/core';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { getFirestore, collection, query, where, onSnapshot } from "firebase/firestore";

import LoginScreen from './components/LoginScreen';
import AppHeader from './components/AppHeader';
import UserManagement from './components/UserManagement';
import ProjectView from './components/ProjectView';
import Navigation from './components/Navigation';
import CreateProjectModal from './components/CreateProjectModal';
import ProjectList from './components/ProjectList';
import ArchivedProjects from './components/ArchivedProjects';

const ProjectDetailsWrapper: React.FC<{
  projects: Project[],
  setProjects: React.Dispatch<React.SetStateAction<Project[]>>,
  setCurrentProject: React.Dispatch<React.SetStateAction<Project | null>>,
  targetTab: 'list' | 'purchase' | 'summary' | null,
  setTargetTab: React.Dispatch<React.SetStateAction<'list' | 'purchase' | 'summary' | null>>
}> = ({ projects, setProjects, setCurrentProject, targetTab, setTargetTab }) => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();

  const project = projects.find(p => p.id === projectId);

  if (!project) {
    return <div className="p-10 text-center text-gray-500">Project not found or loading...</div>;
  }

  const handleSetCurrentProject = (value: React.SetStateAction<Project | null>) => {
    const newVal = value instanceof Function ? value(project) : value;
    if (newVal === null) {
      navigate('/');
    } else {
      setCurrentProject(newVal);
    }
  };

  return (
    <ProjectView
      project={project}
      projects={projects}
      setProjects={setProjects}
      setCurrentProject={handleSetCurrentProject}
      targetTabForProjectView={targetTab}
      setTargetTabForProjectView={setTargetTab}
    />
  );
};

const LoginRedirect: React.FC = () => {
  const { currentUser } = useAuth();
  const location = useLocation();

  if (currentUser) {
    const from = location.state?.from?.pathname || '/';
    return <Navigate to={from} replace />;
  }
  return <LoginScreen />;
};

// Main Application Component
const SecureExpenseTracker: React.FC = () => {
  const { currentUser, authLoading } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const [projects, setProjects] = useState<Project[]>([]);
  const [currentProject, setCurrentProject] = useState<Project | null>(null);
  const [showCreateProject, setShowCreateProject] = useState(false);
  const [targetTabForProjectView, setTargetTabForProjectView] = useState<'list' | 'purchase' | 'summary' | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Re-apply cached items once the last item modal closes (listeners pause while one is open)
  useEffect(() => {
    itemsSync.onResume = () => setProjects(prev => prev.map(proj => {
      const items = itemsSync.items.get(proj.id) || proj.items;
      return items !== proj.items ? { ...proj, items } : proj;
    }));
    return () => { itemsSync.onResume = null; };
  }, []);

  useEffect(() => {
    // Firestore rules require authentication for project reads, so skip the
    // listener entirely while logged out.
    if (!currentUser) {
      setProjects([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    // Add timeout fallback for mobile devices
    const loadingTimeout = setTimeout(() => {
      setIsLoading(false);
    }, 10000); // 10 second timeout

    let unsubscribe: (() => void) | null = null;

    const setupListener = async () => {
      try {
        const db = getFirestore();
        const projectsCollection = collection(db, 'projects');

        // If user is admin, see all.
        // If user is logged in but not admin, see involved.
        // If user is NOT logged in, see ALL (Read Only).
        let q;
        if (currentUser.role === 'admin') {
          q = query(projectsCollection);
        } else {
          q = query(projectsCollection, where('teamsInvolved', 'array-contains', currentUser.team));
        }

        unsubscribe = onSnapshot(q,
          {
            includeMetadataChanges: true // Include pending writes for better real-time updates
          },
          (snapshot) => {
            clearTimeout(loadingTimeout); // Clear timeout on successful load

            // Enhanced debugging for realtime  

            // Store for items tracking
            const projectsMap = new Map<string, Project>();

            const fetchedProjects: Project[] = snapshot.docs.map(docSnapshot => {
              const data = docSnapshot.data();

              // Handle legacy items in document (will be empty after migration)
              const sanitizedData = {
                ...data,
                items: Array.isArray(data.items) ? data.items.map((item: any) => {
                  let attachments = [];

                  if (Array.isArray(item.attachments)) {
                    attachments = item.attachments;
                  } else if (item.attachments && typeof item.attachments === 'object') {
                    const keys = Object.keys(item.attachments);
                    if (keys.length > 0) {
                      attachments = keys.map(key => item.attachments[key]).filter(att => att && typeof att === 'object');
                    }
                  } else if (item.attachments) {
                  }

                  return {
                    ...item,
                    attachments
                  };
                }) : []
              };

              const project = {
                id: docSnapshot.id,
                ...sanitizedData,
              } as Project;

              projectsMap.set(project.id, project);
              return project;
            });

            // Get current project IDs
            const currentProjectIds = new Set(fetchedProjects.map(p => p.id));

            // Get existing listeners map from window
            const existingListeners = itemsSync.listeners;
            const existingItemsData = itemsSync.items;

            // Clean up listeners for removed projects
            existingListeners.forEach((unsub, projectId) => {
              if (!currentProjectIds.has(projectId)) {
                unsub();
                existingListeners.delete(projectId);
                existingItemsData.delete(projectId);
              }
            });

            // Set up listeners only for NEW projects that don't have listeners yet
            fetchedProjects.forEach(p => {
              if (!existingListeners.has(p.id)) {

                // Initialize with document items if not migrated, empty otherwise
                if (p.items.length > 0 && !(p as any).itemsMigratedAt) {
                  existingItemsData.set(p.id, p.items);
                } else {
                  existingItemsData.set(p.id, []);
                }

                const unsubItems = ItemsService.subscribeToItems(
                  p.id,
                  (subcollectionItems) => {
                    existingItemsData.set(p.id, subcollectionItems);

                    // Check if any modal is open - if so, skip the state update
                    // The modal will trigger a fresh sync when it closes
                    if (itemsSync.modalOpenCount > 0) {
                      itemsSync.pendingUpdate = true;
                      return;
                    }

                    // Merge items into projects and update state
                    setProjects(prevProjects => {
                      return prevProjects.map(proj => {
                        const items = existingItemsData.get(proj.id) || proj.items;
                        if (items !== proj.items) {
                          return { ...proj, items };
                        }
                        return proj;
                      });
                    });
                  },
                  () => {}
                );
                existingListeners.set(p.id, unsubItems);
              }
            });

            // Merge items from stored items data
            const projectsWithItems = fetchedProjects.map(p => ({
              ...p,
              items: existingItemsData.get(p.id) || p.items
            }));

            // Sort projects: active/ongoing first, then closed, within each group by creation date (newest first)
            const sortedProjects = projectsWithItems.sort((a, b) => {
              // First priority: status (active/ongoing projects before closed)
              const statusPriorityA = a.status === 'closed' ? 1 : 0;
              const statusPriorityB = b.status === 'closed' ? 1 : 0;

              if (statusPriorityA !== statusPriorityB) {
                return statusPriorityA - statusPriorityB; // Active projects first
              }

              // Second priority: creation date (newest first within same status)
              const dateA = new Date(a.createdAt).getTime();
              const dateB = new Date(b.createdAt).getTime();
              return dateB - dateA; // Descending order (newest first)
            });

            setProjects(sortedProjects);
            setIsLoading(false);
          }, () => {
            clearTimeout(loadingTimeout); // Clear timeout on error
            setProjects([]);
            setIsLoading(false);
          });
      } catch {
        clearTimeout(loadingTimeout); // Clear timeout on setup error
        setProjects([]);
        setIsLoading(false);
      }
    };

    setupListener();

    return () => {
      clearTimeout(loadingTimeout);
      if (unsubscribe) {
        unsubscribe();
      }
      // Also tear down all per-project item listeners so they don't survive a
      // logout/login and feed stale data into the next user's session.
      itemsSync.listeners.forEach(unsub => unsub());
      itemsSync.listeners.clear();
      itemsSync.items.clear();
    };
  }, [currentUser]);

  // Critical: Update currentProject when projects array changes
  useEffect(() => {
    if (currentProject && projects.length > 0) {
      const updatedProject = projects.find(p => p.id === currentProject.id);
      if (updatedProject) {
        // Check if the project data has actually changed to avoid infinite re-renders
        const currentProjectString = JSON.stringify(currentProject);
        const updatedProjectString = JSON.stringify(updatedProject);

        if (currentProjectString !== updatedProjectString) {
          setCurrentProject(updatedProject);
        }
      }
    }
  }, [projects, currentProject]);

  const handleProjectDeletionAction = async (projectToDelete: Project) => {
    if (!currentUser) return;

    const performFullDeletion = async () => {
      try {
        const { db } = await import('./firebaseConfig');
        const { doc, deleteDoc } = await import('firebase/firestore');
        const { AttachmentManager } = await import('./types/attachments');

        let totalFilesDeleted = 0;
        for (const item of projectToDelete.items) {
          if (item.attachments && item.attachments.length > 0) {
            try {
              await AttachmentManager.cleanupAttachments(item.attachments);
              totalFilesDeleted += item.attachments.filter(att => att.storagePath && !att.isLegacy).length;
            } catch (cleanupError) {
            }
          }
        }
        const projectDocRef = doc(db, 'projects', projectToDelete.id.toString());
        await deleteDoc(projectDocRef);

        setProjects(prevProjects => prevProjects.filter(p => p.id !== projectToDelete.id));
        if (currentProject?.id === projectToDelete.id) setCurrentProject(null);
        alert(`${t('project.delete.success')}\n\n${t('project.delete.filesDeleted', { count: totalFilesDeleted, defaultValue: `Deleted ${totalFilesDeleted} associated files.` })}`);

        // Notification logic remains the same
      } catch (error) {
        alert(t('error.generic', { defaultValue: 'An unexpected error occurred during project deletion.' }));
      }
    };

    if (currentUser.role === 'admin') {
      if (window.confirm(t('project.delete.confirm.approve', { projectName: projectToDelete.name }) + "\n" + t('project.delete.warning'))) {
        await performFullDeletion();
      }
      return;
    }

    // Supervisor logic remains the same...
  };

  const location = useLocation();
  const isLoginPage = location.pathname === '/login';
  const isProjectPage = location.pathname.startsWith('/project/');

  if (authLoading || isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4" style={{ minHeight: '100vh', WebkitBackfaceVisibility: 'hidden' }}>
        <div className="text-center max-w-sm mx-auto">
          <div className="animate-spin rounded-full w-16 h-16 border-4 border-blue-500 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg mb-2">
            {authLoading ? 'Initializing Authentication...' : 'Loading Application Data...'}
          </p>
          <p className="text-gray-400 text-sm">
            This may take a moment on mobile devices
          </p>
          {/* Debug info for mobile troubleshooting */}
          <div className="mt-4 text-xs text-gray-300">
            <p>Auth: {authLoading ? 'Loading' : 'Ready'}</p>
            <p>Data: {isLoading ? 'Loading' : 'Ready'}</p>
            <p>User Agent: {navigator.userAgent.includes('iPad') ? 'iPad' : navigator.userAgent.includes('iPhone') ? 'iPhone' : 'Other'}</p>
          </div>
        </div>
      </div>
    );
  }

  // If on login page, render LoginScreen without any wrapper
  if (isLoginPage) {
    return <LoginRedirect />;
  }

  // Everything requires authentication (Firestore rules no longer allow
  // public reads), so logged-out visitors land on the login page. The
  // original destination is preserved so deep links continue after login.
  if (!currentUser) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader />
      {currentUser && !isProjectPage && (
        <Navigation
          onNavigateToProjects={() => navigate('/')}
          onNavigateToAdmin={() => navigate('/admin')}
          onViewArchived={() => navigate('/archived')}
        />
      )}

      <main className="max-w-7xl mx-auto">
        <Routes>
          <Route path="/" element={
            <ProjectList
              projects={projects}
              onViewProject={(p) => navigate(`/project/${p.id}`)}
              onDeleteProject={handleProjectDeletionAction}
              onShowCreateProject={() => setShowCreateProject(true)}
              setProjects={setProjects}
            />
          } />
          <Route path="/project/:projectId" element={
            <ProjectDetailsWrapper
              projects={projects}
              setProjects={setProjects}
              setCurrentProject={setCurrentProject}
              targetTab={targetTabForProjectView}
              setTargetTab={setTargetTabForProjectView}
            />
          } />
          <Route path="/admin" element={
            currentUser?.role === 'admin' ? <UserManagement /> : <Navigate to="/" />
          } />
          <Route path="/archived" element={
            <ArchivedProjects
              projects={projects}
              onViewProject={(p) => navigate(`/project/${p.id}`)}
              onDeleteProject={handleProjectDeletionAction}
              onBack={() => navigate('/')}
              setProjects={setProjects}
            />
          } />
          {/* Fallback route */}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </main>
      {showCreateProject && <CreateProjectModal onClose={() => setShowCreateProject(false)} setProjects={setProjects} />}
    </div>
  );
};

// Main App Component
const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <LanguageProvider>
          <AuthProvider>
            <NotificationProvider>
              <Router>
                <SecureExpenseTracker />
              </Router>
            </NotificationProvider>
          </AuthProvider>
        </LanguageProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
};

export default App;
